<svelte:options />

<script lang="ts">
  import type { FeatureGroup, Feature, A11yLabels } from './sidebar.types';
  import FeatureComponent from './sidebar-feature.nested.svelte';
  import {
    isSidebarOpen,
    selectedFeature,
    selectFeature,
    Selection,
  } from './sidebar-service';
  import { EventHandler } from '../../utilities/EventHandler';

  export let featureGroup: FeatureGroup;
  export let expand: boolean;
  export let a11yLabels: A11yLabels;
  let eventHandler = new EventHandler();
  let content: HTMLElement;

  $: updateSelection(expand, $isSidebarOpen, featureGroup, $selectedFeature);
  $: updateContentCSS(expand, $isSidebarOpen);

  let isSelected = false;

  let label: HTMLElement;

  const mediumIconStyle = 'width: 24px; height: 24px; fill: white;';

  function updateSelection(
    isExpanded: boolean,
    isOpen: boolean,
    group: FeatureGroup,
    selection?: Selection,
  ): void {
    isSelected =
      (selection?.lv0 &&
        group.code === selection?.lv0?.code &&
        (!selection.lv1 || !isExpanded || !isOpen)) ??
      false;
  }

  // Update the CSS programatically to have a nice animation on height between 0px and 'auto'
  function updateContentCSS(isExpanded: boolean, isOpen: boolean): void {
    if (content) {
      content.style.height = '';
      content.style.transition = 'none';
      const startHeight = window.getComputedStyle(content).height;

      content.classList.add(isOpen && isExpanded ? 'is-open' : 'is-close');
      content.classList.remove(isOpen && isExpanded ? 'is-close' : 'is-open');

      const height = window.getComputedStyle(content).height;
      content.style.height = startHeight;

      // wait until the next frame so that everything has time to update before starting the transition
      requestAnimationFrame(() => {
        content.style.transition = '';
        requestAnimationFrame(() => {
          content.style.height = height;
        });
      });

      // Clear the saved height values after the transition
      const handler = () => {
        content.style.height = '';
        content.removeEventListener('transitionend', handler);
      };

      content.addEventListener('transitionend', handler);
    }
  }

  function generateIcon(iconname: string): string {
    return `${iconname} size="2rem"`;
  }

  function handleClick(feature: Feature) {
    if (!feature.disabled) {
      // Only select the feature if it is the last level item
      if (feature.items?.length) {
        eventHandler.dispatch('feature-click', feature);
      } else {
        selectFeature(feature.code);
      }
    }
  }
</script>

<button
  class="mc-feature-group {isSelected ? 'is-selected' : ''} {$isSidebarOpen
    ? 'is-open'
    : 'is-close'}"
  aria-label={featureGroup.label}
  aria-expanded={$isSidebarOpen && expand}
  aria-controls="{featureGroup.code}-list"
  id={featureGroup.code}
>
  <span class="mc-feature-group__container">
    {#if featureGroup.icon}
      {@html `<${generateIcon(
        featureGroup.icon,
      )} class="mc-feature-group__icon" style="${mediumIconStyle}" />`}
    {/if}
    {#if $isSidebarOpen}
      <span class="mc-feature-group__label" bind:this={label}>
        {featureGroup.label}
      </span>
      {#if label?.offsetHeight < label?.scrollHeight}
        <div aria-hidden="true" class="mc-feature-group__label__tooltip">
          <div class="mc-feature-group__label__tooltip__pointer"><div /></div>
          <span>{featureGroup.label}</span>
        </div>
      {/if}
      {#if !expand}
        <svg width="16px" height="16px" fill="white" viewBox="0 0 16 16"
          ><path
            d="M13 7H9V3a1 1 0 00-2 0v4H3a1 1 0 000 2h4v4a1 1 0 002 0V9h4a1 1 0 000-2z"
          /></svg
        >
      {:else}
        <svg width="16px" height="16px" fill="white" viewBox="0 0 16 16"
          ><path d="M12 9H4a1 1 0 010-2h8a1 1 0 010 2z" /></svg
        >
      {/if}
    {/if}
  </span>
</button>
<ul
  class="mc-feature-group__content is-close"
  aria-hidden={!$isSidebarOpen || !expand}
  id="{featureGroup.code}-list"
  bind:this={content}
>
  {#each featureGroup.items as feature}
    <li on:click|stopPropagation={() => handleClick(feature)}>
      <FeatureComponent {feature} {a11yLabels} />
    </li>
  {/each}
</ul>

<style lang="scss">
  @import './sidebar-variables';

  .mc-feature-group {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    font-size: inherit;
    font-family: inherit;
    text-align: initial;
    margin: 0;
    padding: 0;
    overflow: hidden;

    &__container {
      display: flex;
      align-items: center;
      margin: 5px 0;
      padding: 8px 0px;
      @include transition(margin 300ms ease-in-out);
    }

    &__label {
      margin: 0 8px;
      width: 200px;
      min-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;

      &__tooltip {
        opacity: 0;
        transition: 0s opacity;
        background-color: #5b737d;
        border: 1px solid white;
        border-radius: 4px;
        position: absolute;
        pointer-events: none;
        left: calc(100% - 16px);
        padding: 8px 16px;
        width: 188px;

        &__pointer {
          position: absolute;
          top: 50%;
          left: -8px;
          top: calc(50% - 6px);
          width: 0;
          height: 0;
          border-top: 6px solid transparent;
          border-bottom: 6px solid transparent;
          border-right: 8px solid white;

          & div {
            position: relative;
            top: -6px;
            left: 2px;
            width: 0;
            height: 0;
            border-top: 6px solid transparent;
            border-bottom: 6px solid transparent;
            border-right: 8px solid #5b737d;
          }
        }
      }

      &:hover + .mc-feature-group__label__tooltip {
        opacity: 1;
        transition-delay: 1s;
      }
    }

    &__content {
      overflow: hidden;
      @include transition(height 300ms ease-in-out);
      &.is-close {
        height: 0;
      }

      &.is-open {
        height: auto;
      }
    }

    &.is-selected {
      background: $selected-Link;
    }

    &.is-close {
      .mc-feature-group__container {
        margin: 0 20px;
        width: 24px;
      }
    }

    &.is-open {
      .mc-feature-group__container {
        margin: 0 32px;
      }
    }

    &:hover {
      background: #405d68;
    }

    &:focus-visible {
      box-shadow: none;
      border: none;
      outline: none;

      .mc-feature-group__container {
        box-shadow: none;
        border: none;
        border-radius: 6px;
        outline: 1px solid #0b96cc;
      }
    }
  }

  ul,
  li {
    list-style-type: none;
    margin: 0;
    padding: 0;
    display: flex;
    width: 100%;
  }

  ul {
    align-items: start;
    flex-direction: column;
    justify-content: center;

    li {
      flex-direction: column;
      box-sizing: border-box;
    }
  }
</style>
